
import React from "react";
import {
  Users,
  Target,
  Zap,
  Award,
  CheckCircle,
  Heart,
} from "lucide-react";

function About() {
  const values = [
    {
      icon: <Heart size={32} />,
      title: "Qualité Premium",
      description: "Offrir le meilleur streaming 4K sans compromis",
    },
    {
      icon: <Zap size={32} />,
      title: "Performance",
      description: "Vitesse ultra-rapide, zéro buffering garanti",
    },
    {
      icon: <Users size={32} />,
      title: "Service Client",
      description: "Support 24/7 en français pour tous les clients",
    },
    {
      icon: <Award size={32} />,
      title: "Fiabilité",
      description: "Infrastructure sécurisée avec uptime 99.9%",
    },
  ];

  const achievements = [
    { number: "100K+", label: "Clients satisfaits" },
    { number: "25000+", label: "Chaînes disponibles" },
    { number: "15+", label: "Pays couverts" },
    { number: "99.9%", label: "Temps d'activité" },
  ];

  const reasons = [
    {
      title: "25 000+ Chaînes",
      description:
        "Accès complet à toutes les chaînes majeures et chaînes internationales",
    },
    {
      title: "Qualité 4K Ultra HD",
      description:
        "Streaming en haute qualité sur tous les appareils supportés",
    },
    {
      title: "Support 24/7",
      description:
        "Équipe support disponible 24 heures par jour, 7 jours par semaine",
    },
    {
      title: "Zéro Buffering",
      description:
        "Infrastructure optimisée pour un streaming fluide sans interruption",
    },
    {
      title: "Multi-Appareils",
      description:
        "Utilisez un compte sur 4 appareils simultanément",
    },
    {
      title: "Essai Gratuit",
      description:
        "7 jours gratuits pour tester notre service sans engagement",
    },
  ];

  return (
    <div className="w-full bg-slate-950 text-white">

      {/* ================= HERO ================= */}
      <section className="relative overflow-hidden px-4 py-24 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-b from-blue-950/40 via-slate-950 to-slate-950" />

        <div className="relative mx-auto max-w-7xl text-center">
          <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            À propos de SERVICE{" "}
            <span className="text-blue-500">IPTV</span>
          </h1>

          <p className="mx-auto mt-6 max-w-3xl text-lg leading-8 text-gray-400 sm:text-xl">
            Votre destination ultime pour le streaming IPTV de qualité
            premium au France
          </p>
        </div>
      </section>

      {/* ================= OUR STORY ================= */}
      <section className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">

          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2">

            {/* Text */}
            <div>
              <h2 className="text-3xl font-bold sm:text-4xl">
                Notre Histoire
              </h2>

              <div className="mt-6 space-y-5 text-base leading-7 text-gray-400">
                <p>
                  SERVICE IPTV a été fondée en 2019 avec une vision simple
                  mais puissante : rendre le streaming IPTV de haute qualité
                  accessible à tous les Canadiens. Déçus par les services
                  existants, nos fondateurs ont décidé de créer quelque chose
                  de meilleur.
                </p>

                <p>
                  Aujourd'hui, nous servons plus de 100 000 clients satisfaits
                  à travers le france avec plus de 25 000 chaînes et un contenu
                  VOD impressionnant. Notre engagement envers l'excellence
                  n'a jamais faibli.
                </p>

                <p>
                  Nous continuons d'innover, d'investir dans l'infrastructure
                  et de perfectionner notre service pour offrir l'expérience
                  de streaming la meilleure possible.
                </p>
              </div>
            </div>

            {/* Image / Placeholder */}
            <div
              className="
                flex
                min-h-[320px]
                items-center
                justify-center
                rounded-3xl
                border border-white/10
                bg-gradient-to-br
                from-blue-600/20
                via-purple-600/10
                to-white/5
                shadow-2xl
                backdrop-blur-xl
              "
            >
              <div className="text-8xl drop-shadow-2xl">
                📺
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ================= MISSION & VISION ================= */}
      <section className="border-y border-white/5 bg-white/[0.02] px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">

            {/* Mission */}
            <div
              className="
                rounded-3xl
                border border-white/10
                bg-white/5
                p-8
                shadow-xl
                backdrop-blur-xl
                transition-all
                duration-300
                hover:-translate-y-2
                hover:border-blue-500/40
              "
            >
              <div
                className="
                  mb-6
                  flex h-16 w-16
                  items-center justify-center
                  rounded-2xl
                  bg-blue-500/10
                  text-blue-400
                "
              >
                <Target size={40} />
              </div>

              <h3 className="text-2xl font-bold">
                Notre Mission
              </h3>

              <p className="mt-4 leading-7 text-gray-400">
                Fournir un service IPTV de premier ordre offrant accès à des
                milliers de chaînes et de contenu à prix compétitif, avec un
                support client exceptionnel et une technologie fiable.
              </p>
            </div>

            {/* Vision */}
            <div
              className="
                rounded-3xl
                border border-white/10
                bg-white/5
                p-8
                shadow-xl
                backdrop-blur-xl
                transition-all
                duration-300
                hover:-translate-y-2
                hover:border-purple-500/40
              "
            >
              <div
                className="
                  mb-6
                  flex h-16 w-16
                  items-center justify-center
                  rounded-2xl
                  bg-purple-500/10
                  text-purple-400
                "
              >
                <Zap size={40} />
              </div>

              <h3 className="text-2xl font-bold">
                Notre Vision
              </h3>

              <p className="mt-4 leading-7 text-gray-400">
                Devenir le fournisseur IPTV numéro un en Amérique du Nord en
                offrant une expérience de streaming incomparable, de
                l'innovation constante et un dévouement envers la satisfaction
                client.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ================= CORE VALUES ================= */}
      <section className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">

          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold sm:text-4xl">
              Nos Valeurs Fondamentales
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">

            {values.map((value, index) => (
              <div
                key={index}
                className="
                  group
                  rounded-2xl
                  border border-white/10
                  bg-white/5
                  p-6
                  text-center
                  shadow-lg
                  backdrop-blur-xl
                  transition-all
                  duration-300
                  hover:-translate-y-2
                  hover:border-blue-500/30
                  hover:bg-white/[0.08]
                "
              >
                <div
                  className="
                    mx-auto
                    flex h-16 w-16
                    items-center justify-center
                    rounded-2xl
                    bg-blue-500/10
                    text-blue-400
                    transition-transform
                    duration-300
                    group-hover:scale-110
                  "
                >
                  {value.icon}
                </div>

                <h3 className="mt-5 text-xl font-bold">
                  {value.title}
                </h3>

                <p className="mt-3 text-sm leading-6 text-gray-400">
                  {value.description}
                </p>
              </div>
            ))}

          </div>
        </div>
      </section>

      {/* ================= ACHIEVEMENTS ================= */}
      <section className="border-y border-white/5 bg-white/[0.02] px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">

          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold sm:text-4xl">
              Nos Réalisations
            </h2>
          </div>

          <div className="grid grid-cols-2 gap-5 md:grid-cols-4">

            {achievements.map((achievement, index) => (
              <div
                key={index}
                className="
                  rounded-2xl
                  border border-white/10
                  bg-white/5
                  p-6
                  text-center
                  shadow-lg
                  backdrop-blur-xl
                "
              >
                <div className="text-3xl font-extrabold text-blue-400 sm:text-4xl">
                  {achievement.number}
                </div>

                <div className="mt-2 text-sm text-gray-400">
                  {achievement.label}
                </div>
              </div>
            ))}

          </div>
        </div>
      </section>

      {/* ================= WHY CHOOSE US ================= */}
      <section className="px-4 py-20 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-7xl">

          <div className="mb-12 text-center">
            <h2 className="text-3xl font-bold sm:text-4xl">
              Pourquoi Nous Choisir ?
            </h2>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">

            {reasons.map((reason, index) => (
              <div
                key={index}
                className="
                  rounded-2xl
                  border border-white/10
                  bg-white/5
                  p-6
                  transition-all
                  duration-300
                  hover:-translate-y-1
                  hover:border-blue-500/30
                  hover:bg-white/[0.08]
                "
              >
                <div className="flex items-start gap-4">

                  <CheckCircle
                    size={24}
                    className="
                      mt-1
                      flex-shrink-0
                      text-green-400
                    "
                  />

                  <div>
                    <h3 className="text-lg font-bold">
                      {reason.title}
                    </h3>

                    <p className="mt-2 text-sm leading-6 text-gray-400">
                      {reason.description}
                    </p>
                  </div>

                </div>
              </div>
            ))}

          </div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="px-4 py-20 sm:px-6 lg:px-8">
        <div
          className="
            mx-auto
            max-w-5xl
            overflow-hidden
            rounded-3xl
            border border-blue-500/20
            bg-gradient-to-r
            from-blue-600/20
            via-purple-600/20
            to-blue-600/10
            px-6 py-16
            text-center
            shadow-2xl
            backdrop-blur-xl
            sm:px-10
          "
        >
          <h2 className="text-3xl font-bold sm:text-4xl lg:text-5xl">
            Prêt à Rejoindre Nous ?
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-gray-400 sm:text-lg">
            Commencez votre essai gratuit de 7 jours dès maintenant
          </p>

          <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">

            {/* Free Trial */}
            <button
              type="button"
              onClick={() =>
                window.open(
                  "https://wa.me/447308508638",
                  "_blank",
                  "noopener,noreferrer"
                )
              }
              className="
                w-full
                rounded-xl
                bg-blue-600
                px-8 py-3.5
                font-semibold
                text-white
                shadow-lg
                shadow-blue-600/20
                transition-all
                duration-300
                hover:-translate-y-1
                hover:bg-blue-500
                sm:w-auto
              "
            >
              Essai Gratuit
            </button>

            {/* Contact */}
            <button
              type="button"
              onClick={() =>
                window.open(
                  "https://abonnement-iptvfr.fr/contact",
                  "_self"
                )
              }
              className="
                w-full
                rounded-xl
                border border-white/20
                bg-white/5
                px-8 py-3.5
                font-semibold
                text-white
                transition-all
                duration-300
                hover:-translate-y-1
                hover:bg-white/10
                sm:w-auto
              "
            >
              Nous Contacter
            </button>

          </div>
        </div>
      </section>

    </div>
  );
}

export default About;
